import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage } from "@/app/api";
import { exportFilterOf, exportTooWide, filterSummary, messageExports, rangeLabel, useExportNames, type MessageExport } from "@/app/exports";
import { FormDrawer } from "@/app/FormDrawer";
import type { MessageFilter } from "@/app/messages";
import { Alert, Button, Checkbox, Grid, KeyValue, SectionTitle } from "@/ui";

interface Props {
  /** 消息记录页已生效的条件，含时间范围 from、to。 */
  filter: MessageFilter;
  onClose: () => void;
  onCreated: (e: MessageExport) => void;
}

/** 导出 Excel 的表单（决策 D202）：显示要导出的时间范围与条件，勾选字段，建任务后由调用方打开任务列表看进度。
 * 字段目录与缺省勾选来自服务端；导出文件的列按目录顺序排，与勾选顺序无关。 */
export function MessageExportForm({ filter, onClose, onCreated }: Props) {
  const names = useExportNames();
  const qc = useQueryClient();
  const catalog = useQuery({ queryKey: ["message-exports", "fields"], queryFn: messageExports.fields, staleTime: Infinity });
  // 还没动过勾选时跟着缺省走，目录加载完就是缺省字段
  const [picked, setPicked] = useState<string[] | null>(null);
  const fields = catalog.data?.fields ?? [];
  const selected = picked ?? catalog.data?.defaults ?? [];
  const create = useMutation({
    mutationFn: () => messageExports.create(filter, fields.map((f) => f.key).filter((k) => selected.includes(k))),
    onSuccess: (e) => { void qc.invalidateQueries({ queryKey: ["message-exports"] }); onCreated(e); },
  });
  const conditions = filterSummary(exportFilterOf(filter), names);
  const tooWide = exportTooWide(filter);
  const toggle = (key: string, on: boolean) => setPicked(on ? [...selected, key] : selected.filter((k) => k !== key));

  return (
    <FormDrawer open title="导出 Excel" submitLabel="创建导出任务" saving={create.isPending || !selected.length || tooWide} error={create.error && errorMessage(create.error)}
      onSubmit={() => create.mutate()} onClose={onClose}>
      <div className="ax-section">
        <KeyValue items={[
          { label: "时间范围", value: filter.from && filter.to ? rangeLabel(filter.from, filter.to) : "—" },
          { label: "筛选条件", value: conditions.length ? conditions.join("；") : "无" },
        ]} />
        {tooWide && <Alert tone="warning" title="时间范围太大">没有号码、账号或 message_id 时最多导出 7 天：缩小时间范围，或加上这些条件。</Alert>}
        <span className="ax-muted">按页面上已生效的条件导出，一条消息一行，最多 100 万条；任务在后台执行，完成后在顶栏的“任务中心”里下载，保留 7 天。</span>
      </div>
      <div className="ax-section">
        <SectionTitle>导出字段</SectionTitle>
        {catalog.error && <Alert tone="danger" title="字段加载失败">{errorMessage(catalog.error)}</Alert>}
        <div className="ax-row">
          <Button size="sm" variant="ghost" onClick={() => setPicked(fields.map((f) => f.key))}>全选</Button>
          <Button size="sm" variant="ghost" onClick={() => setPicked(null)}>恢复默认</Button>
        </div>
        <Grid columns={2}>
          {fields.map((f) => <Checkbox key={f.key} label={f.label} checked={selected.includes(f.key)} onChange={(on) => toggle(f.key, on)} />)}
        </Grid>
        {!selected.length && fields.length > 0 && <Alert tone="warning" title="没有选字段">至少选一个字段。</Alert>}
      </div>
    </FormDrawer>
  );
}
